import { useEffect, useRef } from 'react';
import { Toast, useToast } from '@zillow/constellation-10';
import StringConstants from '../constants/StringConstants';

const TOAST_KEY = 'lead-management-load-more-error';

type UseLoadMoreErrorToastParams = {
  isFetchNextPageError: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  /**
   * Identity of the paginated query the toast describes (stringified query key). When it changes
   * (folder/filter switch) the toast is stale — the view stays mounted on that switch, so this is
   * the signal that clears it.
   */
  queryKey: string;
  /**
   * Called when the toast is cleared without real recovery (query switch or unmount), so callers
   * can reset the errored query and the next visit to it starts with a fresh fetch rather than
   * re-entering the error state.
   */
  onReset?: () => void;
};

/**
 * Surfaces a persistent, dismissible critical toast when loading the next page of leads fails.
 * Loading more is passive (scroll-triggered, no button), so a transient toast is easy to miss and
 * the list would just look complete; the toast persists until the user dismisses it or the missing
 * page actually loads.
 *
 * Dismissal is gated on real recovery, not on `isFetchNextPageError` clearing: React Query drops
 * that flag the instant ANY refetch starts (e.g. the invalidations useRealtimeUpdates fires on WS
 * events and poll ticks) — regardless of the refetch's outcome, and a plain refetch only reloads
 * already-loaded pages, never the missing next one. Keying off the bare flag would let routine
 * background activity clear the toast while the user is still a page behind — the silent failure
 * this toast exists to surface. Mirrors the inbox's ConversationList load-more error toast.
 */
export function useLoadMoreErrorToast({
  isFetchNextPageError,
  isFetchingNextPage,
  hasNextPage,
  queryKey,
  onReset,
}: UseLoadMoreErrorToastParams): void {
  const { enqueueToast, dismiss, toasts } = useToast();

  // Whether we've enqueued a toast that hasn't been dismissed yet (by us or the user). Tracked
  // ourselves rather than derived by searching `toasts`: the provider's `toasts` only reflects
  // *active* toasts — with enough unrelated toasts already showing (the provider's `maxSize`),
  // ours sits invisible in the pending queue, where a key lookup would miss it and wrongly
  // enqueue a duplicate.
  const enqueuedRef = useRef(false);

  // Whether the user manually dismissed the toast during the current error episode, so we don't
  // re-enqueue it while the same error persists. Cleared once real recovery is confirmed.
  const dismissedRef = useRef(false);

  // Whether the missing next page has actually loaded (or there's nothing left to load) since the
  // toast was shown. Persists across renders so it survives the toast being pending (invisible in
  // `toasts`) until it becomes active and can actually be dismissed.
  const recoveredRef = useRef(false);

  // Previous render's isFetchingNextPage, so the effect below can detect the moment a next-page
  // fetch attempt finishes (see justFinishedFetchingNextPage).
  const wasFetchingNextPageRef = useRef(false);

  const onResetRef = useRef(onReset);
  onResetRef.current = onReset;

  // The toast is scoped to one paginated query (one folder + filter combination). When the query
  // identity changes, the toast describes the previous query's pagination — drop it, reset the
  // per-episode state so a lingering manual dismiss doesn't suppress a genuine failure in the new
  // query, and reset the errored query so the next visit to it starts fresh.
  const queryKeyRef = useRef(queryKey);
  useEffect(() => {
    if (queryKeyRef.current === queryKey) {
      return;
    }
    queryKeyRef.current = queryKey;
    const hadErrorEpisode = enqueuedRef.current || dismissedRef.current;
    enqueuedRef.current = false;
    dismissedRef.current = false;
    recoveredRef.current = false;
    const staleToast = toasts.find((toast) => toast?.node?.key === TOAST_KEY);
    if (staleToast) {
      dismiss(staleToast.id);
    }
    if (hadErrorEpisode) {
      onResetRef.current?.();
    }
  }, [queryKey, toasts, dismiss]);

  useEffect(() => {
    const justFinishedFetchingNextPage = wasFetchingNextPageRef.current && !isFetchingNextPage;
    wasFetchingNextPageRef.current = isFetchingNextPage;

    if (isFetchNextPageError) {
      recoveredRef.current = false;
      if (enqueuedRef.current || dismissedRef.current) {
        return;
      }
      enqueuedRef.current = true;
      enqueueToast(
        <Toast
          key={TOAST_KEY}
          tone="critical"
          onClose={() => {
            dismissedRef.current = true;
            enqueuedRef.current = false;
          }}
        >
          {StringConstants.LEAD_MANAGEMENT_LOAD_MORE_ERROR}
        </Toast>,
        { duration: 0 }, // persist until dismissed or the missing page actually loads
      );
      return;
    }

    if (justFinishedFetchingNextPage || !hasNextPage) {
      recoveredRef.current = true;
      dismissedRef.current = false;
    }

    if (!enqueuedRef.current || !recoveredRef.current) {
      return;
    }

    const existingToast = toasts.find((toast) => toast?.node?.key === TOAST_KEY);
    if (!existingToast) {
      // Still pending (invisible in `toasts`) — nothing to dismiss yet. `toasts` is a dependency,
      // so promotion to active re-runs this effect and the toast is dismissed then.
      return;
    }
    dismiss(existingToast.id);
    enqueuedRef.current = false;
    // Recovery means the missing page actually loaded — no onReset here; invalidating right after
    // fresh data landed would just trigger a redundant refetch.
  }, [isFetchNextPageError, isFetchingNextPage, hasNextPage, toasts, enqueueToast, dismiss]);

  // Keep the latest toast-queue accessors in a ref so the unmount cleanup below can read current
  // state without re-running (and dismissing the toast) every time the queue changes.
  const toastQueueRef = useRef({ toasts, dismiss });
  useEffect(() => {
    toastQueueRef.current = { toasts, dismiss };
  });

  // If the view unmounts mid-error-episode, dismiss the toast (it lives in the app-level
  // ToastProvider, which outlives this route) and reset the errored query so the next visit starts
  // with a fresh fetch. Best-effort: a toast still pending (not yet promoted to active) can't be
  // found in `toasts` and would linger — a pre-existing limitation shared with the inbox toast.
  useEffect(
    () => () => {
      if (!enqueuedRef.current && !dismissedRef.current) {
        return;
      }
      const { toasts: currentToasts, dismiss: currentDismiss } = toastQueueRef.current;
      const toast = currentToasts.find((t) => t?.node?.key === TOAST_KEY);
      if (toast) {
        currentDismiss(toast.id);
      }
      onResetRef.current?.();
    },
    [],
  );
}
